import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { ApiService } from '@gray/domain/http/api.service';

export interface ICoachGoogleStatus {
  connected: boolean;
  googleEmail: string | null;
  connectedAt: string | null;
  tokenExpiresAt: string | null;
}

/**
 * The coach's Google connection. It is what lets the panel put a meeting on their calendar and
 * hand out a Meet link, so the account screen owns connecting and disconnecting it.
 */
@Injectable({providedIn: 'root'})
export class CoachGoogleService {

  private apiPrefix = '/coach/google';
  private readonly isBrowser: boolean;

  constructor(
    private api: ApiService,
    @Inject(PLATFORM_ID) platformId: any,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  getStatus(): Observable<any> {
    return this.api.get(`${this.apiPrefix}/status`);
  }

  /**
   * Returns the Google consent screen to send the browser to. The connect button lives on more than
   * one screen, so the page to come back to travels with the request instead of being fixed on the
   * API side; the callback appends `?status=connected|error` to it.
   *
   * @param returnUrl absolute address to come back to, defaulting to the current page.
   */
  getConnectUrl(returnUrl?: string): Observable<any> {
    const encoded = this.encodeReturnUrl(returnUrl ?? this.currentUrl());
    const queryString = encoded ? `?returnUrl=${encodeURIComponent(encoded)}` : '';

    return this.api.get(`${this.apiPrefix}/connect-url`, queryString);
  }

  disconnect(): Observable<any> {
    return this.api.delete(this.apiPrefix);
  }

  /**
   * The page as it should be returned to: a previous `status` is dropped so reconnecting does not
   * pile up outcomes in the address bar. Empty while server-side rendering — the API then answers
   * without a redirect target, and the connect flow only ever starts from a click anyway.
   */
  private currentUrl(): string {
    if (!this.isBrowser) {
      return '';
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('status');
    return url.toString();
  }

  /** Base64 the API can decode as UTF-8 — `btoa` alone rejects non-Latin1 characters. */
  private encodeReturnUrl(url: string): string {
    if (!url) {
      return '';
    }

    const bytes = new TextEncoder().encode(url);
    const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
    return btoa(binary);
  }
}
